import path from 'path';
import { fileExists, readJson } from './utils/fs.js';
import { logger } from './lib/logger.js';
import type { Config, ProjectConfig } from './types/index.js';
import { ProjectConfigSchema } from './types/index.js';
import { findCsprojFiles, detectNamespaces } from './utils/dotnet.js';

// Default configuration - projectPath will be resolved dynamically
const defaultConfig: Config = {
  version: '1.0.0',
  smartstack: {
    // Will be resolved in getConfig() - never use a hard-coded path
    projectPath: '',
    apiUrl: process.env.SMARTSTACK_API_URL || 'https://localhost:5001',
    apiEnabled: process.env.SMARTSTACK_API_ENABLED !== 'false',
  },
  conventions: {
    schemas: {
      platform: 'core',
      extensions: 'extensions',
    },
    tablePrefixes: [
      'auth_', 'nav_', 'usr_', 'ai_', 'cfg_', 'wkf_',
      'support_', 'entra_', 'ref_', 'loc_', 'lic_', 'tenant_'
    ],
    scopeTypes: ['Core', 'Extension', 'Partner', 'Community'],
    migrationFormat: '{context}_v{version}_{sequence}_{Description}',
    namespaces: {
      // Empty = auto-detect from .csproj files
      // Can be overridden in config file for custom namespaces
      domain: '',
      application: '',
      infrastructure: '',
      api: '',
    },
    servicePattern: {
      interface: 'I{Name}Service',
      implementation: '{Name}Service',
    },
  },
  efcore: {
    contexts: [
      {
        name: 'ApplicationDbContext',
        projectPath: 'auto-detect',
        migrationsFolder: 'Migrations',
      },
    ],
    validation: {
      checkModelSnapshot: true,
      checkMigrationOrder: true,
      requireBuildSuccess: true,
    },
  },
  scaffolding: {
    outputPath: 'auto-detect',
    templates: {
      service: 'templates/service-extension.cs.hbs',
      entity: 'templates/entity-extension.cs.hbs',
      controller: 'templates/controller.cs.hbs',
      component: 'templates/component.tsx.hbs',
    },
  },
  // Will be loaded from .smartstack/config.json if present
  projectConfig: undefined,
  // Default to 'core', will be overridden by projectConfig.dbContext if present
  defaultDbContext: 'core',
};

let cachedConfig: Config | null = null;

/**
 * Resolve the SmartStack project path
 * Priority: 1. SMARTSTACK_PROJECT_PATH env var, 2. Config file, 3. Current working directory
 */
function resolveProjectPath(configPath?: string): string {
  // Priority 1: Environment variable
  if (process.env.SMARTSTACK_PROJECT_PATH) {
    return process.env.SMARTSTACK_PROJECT_PATH;
  }

  // Priority 2: Config file value (if provided and not empty)
  if (configPath && configPath.trim()) {
    return configPath;
  }

  // Priority 3: Current working directory
  const cwd = process.cwd();
  logger.warn('No SMARTSTACK_PROJECT_PATH configured, using current directory', { cwd });
  return cwd;
}

/**
 * Get the configuration, loading from file if available
 */
export async function getConfig(): Promise<Config> {
  if (cachedConfig) {
    return cachedConfig;
  }

  // Try to load from config file
  const configPath = path.join(process.cwd(), 'config', 'default-config.json');

  if (await fileExists(configPath)) {
    try {
      const fileConfig = await readJson<Partial<Config>>(configPath);
      cachedConfig = mergeConfig(defaultConfig, fileConfig);
      logger.info('Configuration loaded from file', { path: configPath });
    } catch (error) {
      logger.warn('Failed to load config file, using defaults', { error });
      cachedConfig = { ...defaultConfig };
    }
  } else {
    logger.debug('No config file found, using defaults');
    cachedConfig = { ...defaultConfig };
  }

  // Resolve project path dynamically (never use hard-coded path)
  cachedConfig.smartstack.projectPath = resolveProjectPath(
    cachedConfig.smartstack.projectPath
  );

  // Load .smartstack/config.json if present (created by CLI during ss init)
  const smartstackConfigPath = path.join(
    cachedConfig.smartstack.projectPath,
    '.smartstack',
    'config.json'
  );

  if (await fileExists(smartstackConfigPath)) {
    try {
      const projectConfigRaw = await readJson<ProjectConfig>(smartstackConfigPath);
      const projectConfig = ProjectConfigSchema.parse(projectConfigRaw);
      cachedConfig.projectConfig = projectConfig;
      cachedConfig.defaultDbContext = projectConfig.dbContext;
      logger.info('Project config loaded from .smartstack/config.json', {
        projectType: projectConfig.projectType,
        dbContext: projectConfig.dbContext,
      });
    } catch (error) {
      logger.warn('Failed to load .smartstack/config.json, using defaults', { error });
    }
  } else {
    // No .smartstack/config.json = platform/socle project = core context
    logger.debug('No .smartstack/config.json found, assuming platform project (core context)');
  }

  // Auto-detect namespaces from .csproj files if not configured
  const namespacesEmpty = !cachedConfig.conventions.namespaces.domain ||
    !cachedConfig.conventions.namespaces.application ||
    !cachedConfig.conventions.namespaces.infrastructure ||
    !cachedConfig.conventions.namespaces.api;

  if (namespacesEmpty && cachedConfig.smartstack.projectPath) {
    try {
      const csprojFiles = await findCsprojFiles(cachedConfig.smartstack.projectPath);
      const detected = await detectNamespaces(csprojFiles);

      if (detected) {
        // Only override empty values, keep user-configured ones
        if (!cachedConfig.conventions.namespaces.domain) {
          cachedConfig.conventions.namespaces.domain = detected.domain;
        }
        if (!cachedConfig.conventions.namespaces.application) {
          cachedConfig.conventions.namespaces.application = detected.application;
        }
        if (!cachedConfig.conventions.namespaces.infrastructure) {
          cachedConfig.conventions.namespaces.infrastructure = detected.infrastructure;
        }
        if (!cachedConfig.conventions.namespaces.api) {
          cachedConfig.conventions.namespaces.api = detected.api;
        }
        logger.info('Namespaces auto-detected from project', { baseNamespace: detected.baseNamespace });
      } else {
        logger.warn('Could not auto-detect namespaces from .csproj files. Configure manually in config file.');
      }
    } catch (error) {
      logger.warn('Failed to auto-detect namespaces', { error });
    }
  }

  // Override with environment variables
  if (process.env.SMARTSTACK_API_URL) {
    cachedConfig.smartstack.apiUrl = process.env.SMARTSTACK_API_URL;
  }

  // Validate project path exists
  if (!cachedConfig.smartstack.projectPath) {
    throw new Error(
      'SmartStack project path not configured. Set SMARTSTACK_PROJECT_PATH environment variable or configure in config file.'
    );
  }

  return cachedConfig;
}

/**
 * Deep merge configuration objects
 */
function mergeConfig(base: Config, override: Partial<Config>): Config {
  return {
    ...base,
    ...override,
    smartstack: {
      ...base.smartstack,
      ...override.smartstack,
    },
    conventions: {
      ...base.conventions,
      ...override.conventions,
      schemas: {
        ...base.conventions.schemas,
        ...override.conventions?.schemas,
      },
      scopeTypes: override.conventions?.scopeTypes || base.conventions.scopeTypes,
      namespaces: {
        ...base.conventions.namespaces,
        ...override.conventions?.namespaces,
      },
      servicePattern: {
        ...base.conventions.servicePattern,
        ...override.conventions?.servicePattern,
      },
    },
    efcore: {
      ...base.efcore,
      ...override.efcore,
      contexts: override.efcore?.contexts || base.efcore.contexts,
      validation: {
        ...base.efcore.validation,
        ...override.efcore?.validation,
      },
    },
    scaffolding: {
      ...base.scaffolding,
      ...override.scaffolding,
      templates: {
        ...base.scaffolding.templates,
        ...override.scaffolding?.templates,
      },
    },
  };
}

/**
 * Get SmartStack project path
 */
export function getProjectPath(config: Config): string {
  return config.smartstack.projectPath;
}

/**
 * Clear cached configuration (for testing)
 */
export function clearConfigCache(): void {
  cachedConfig = null;
}
